import axios from 'axios';
import { clearAuth, getAuthToken } from '../utils/authStorage';

// Allow overriding API host via env
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url?.endsWith('/auth/login');

    if (error.response?.status === 401 && !isLoginRequest) {
      clearAuth();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (login, password, rememberMe = false) => api.post('/auth/login', { login, password, rememberMe });
export const getSessions = () => api.get('/auth/sessions');
export const deleteSession = (sessionId) => api.delete(`/auth/sessions/${sessionId}`);
export const logout = () => api.post('/auth/logout');
export const changePassword = (currentPassword, newPassword) => api.post('/auth/change-password', { currentPassword, newPassword });
export const getBlockedAttemptCounts = () => api.get('/auth/blocked-attempts/counts');
export const getBlockedIps = () => api.get('/auth/blocked-attempts/ips');
export const getBlockedLogins = () => api.get('/auth/blocked-attempts/logins');

// Generic CRUD operations
const createCRUD = (endpoint) => ({
  getAll: (params) => api.get(endpoint, { params }),
  getById: (id) => api.get(`${endpoint}/${id}`),
  create: (data) => api.post(endpoint, data),
  update: (id, data) => api.put(`${endpoint}/${id}`, data),
  delete: (id) => api.delete(`${endpoint}/${id}`),
});

// Export all entity APIs
export const users = createCRUD('/users');
export const currencies = createCRUD('/currencies');
export const timezones = createCRUD('/timezones');
export const countries = createCRUD('/countries');
export const airports = createCRUD('/airports');
export const airportPairs = createCRUD('/airport-pairs');
export const routes = createCRUD('/routes');
export const carriers = createCRUD('/carriers');
export const carrierCodes = createCRUD('/carrier-codes');
export const agents = createCRUD('/agents');
export const planes = createCRUD('/planes');
export const planeModels = createCRUD('/plane-models');
export const planeModelsHistory = {
  ...createCRUD('/plane-models-history'),
  getByPlane: (planeId) => api.get(`/plane-models-history/plane/${planeId}`)
};
export const planeRegs = {
  ...createCRUD('/plane-regs'),
  getByPlane: (planeId) => api.get(`/plane-regs/plane/${planeId}`)
};
export const flights = createCRUD('/flights');
export const legs = createCRUD('/legs');
export const legsSchedule = {
  ...createCRUD('/legs-schedule'),
  getChain: (initId, lastId) => api.get(`/legs-schedule/chain/${initId}/${lastId}`)
};
export const legsFlights = createCRUD('/legs-flights');
export const manualFlights = createCRUD('/manual-flights');
export const passengers = createCRUD('/passengers');
export const passengerDocuments = createCRUD('/passenger-documents');
export const bookings = createCRUD('/bookings');
export const tickets = createCRUD('/tickets');
export const ticketSegments = createCRUD('/ticket-segments');
export const payments = createCRUD('/payments');
export const flightsStat = createCRUD('/flights-stat');
export const flightsStatSummary = {
  get: (params) => api.get('/flights-stat/summary', { params })
};

export default api;
